import React from 'react';

const IDAComplianceSection = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            IDA – Compliance, die in den Arbeitsalltag passt
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Compliance, die im Flow passiert – nicht irgendwann.
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-teal-700 to-blue-500 mx-auto rounded-full mt-6"></div>
        </div>

        {/* Top Row: 50/50 Split - Warum IDA? + Intro/Founder Statement */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">

          {/* LEFT: Warum IDA? */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-200 h-full">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">
              Warum IDA?
            </h3>

            <div className="space-y-5">
              <div className="flex items-start gap-4 group">
                <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-teal-200 transition-colors">
                  <svg className="w-5 h-5 text-teal-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Im Arbeitsfluss statt nebenher</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Aufgaben, Checklisten und Hinweise erscheinen genau dann, wenn sie gebraucht werden – während der Schicht.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-teal-200 transition-colors">
                  <svg className="w-5 h-5 text-teal-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Null Papierkram</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Kein Ausdrucken, Abheften, Nachtelefonieren. Alles digital, auffindbar, revisionssicher.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-teal-200 transition-colors">
                  <svg className="w-5 h-5 text-teal-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Automatisierte Nachweise</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Dokumentation und Auswertung laufen im Hintergrund – audit-ready auf Knopfdruck.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-teal-200 transition-colors">
                  <svg className="w-5 h-5 text-teal-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Transparenz für alle Rollen</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Mitarbeitende wissen, was zu tun ist; Betreiber und Verantwortliche sehen Erfüllungsgrad, Risiken und Trends.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1 group-hover:bg-teal-200 transition-colors">
                  <svg className="w-5 h-5 text-teal-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Weniger Fehler, mehr Sicherheit</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Erinnerungen, Eskalationen und klare Verantwortlichkeiten senken Compliance-Risiken spürbar.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Intro Text + Founder Statement Combined */}
          <div className="space-y-6">
            {/* Intro Text Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-200">
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Compliance im Alltag</h3>
                <p className="text-gray-600">Über 20 Jahre Branchenerfahrung</p>
              </div>

              <p className="text-gray-700 leading-relaxed">
                Aus über 20 Jahren Erfahrung in Sportwetten und Spielhallen haben wir eines gelernt: Die Frage ist nicht,
                ob Compliance sinnvoll ist, sondern <span className="font-bold text-teal-800">wie sie in die tägliche Arbeit integriert wird</span>.
                Eine Schulung alle zwei Jahre reicht nicht. IDA macht Compliance präsent, leicht umsetzbar und jederzeit nachweisbar.
              </p>
            </div>

            {/* Founder Statement Card */}
            <div className="bg-gradient-to-br from-teal-50 via-blue-50 to-white rounded-2xl shadow-xl p-8 border-2 border-teal-700 relative overflow-hidden">
              {/* Decorative Quote Mark */}
              <div className="absolute top-4 right-4 opacity-10">
                <svg className="w-24 h-24 text-teal-700" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z"/>
                </svg>
              </div>

              <div className="relative">
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src="/images/foto-sebastian-funke.png"
                    alt="Sebastian Funke"
                    className="w-16 h-16 rounded-full object-cover flex-shrink-0 shadow-lg border-2 border-teal-700"
                  />
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900">Sebastian Funke</h4>
                    <p className="text-gray-600 flex items-center gap-2">
                      <svg className="w-4 h-4 text-teal-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Founder & CEO
                    </p>
                  </div>
                </div>

                <blockquote className="text-gray-700 leading-relaxed italic border-l-4 border-teal-700 pl-4">
                  Das Problem war nie der fehlende Wille. Es fehlte das richtige Werkzeug: zu viel Papier, zu viele Listen,
                  zu viel Suchen. IDA integriert Compliance nahtlos in den Arbeitsalltag – ohne Bürokratie, mit sauberer Dokumentation.
                </blockquote>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row: 100% Width - Wie IDA den Unterschied macht */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-200">
          {/* Header */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900">
              Wie IDA den Unterschied macht
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <h4 className="font-bold text-teal-800 mb-1">Präsent</h4>
              <p className="text-gray-700 leading-relaxed">
                Compliance-Aufgaben erscheinen kontextbezogen während der Arbeit, nicht in veralteten Handbüchern.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-teal-800 mb-1">Einfach</h4>
              <p className="text-gray-700 leading-relaxed">
                Intuitive Checklisten, geführte Maßnahmen, Fotobelege und Notizen in Minuten erledigt.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-teal-800 mb-1">Nachweisbar</h4>
              <p className="text-gray-700 leading-relaxed">
                Lückenlose Historie, Reports und Export jederzeit prüf- und belegbar.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-teal-800 mb-1">Skalierbar</h4>
              <p className="text-gray-700 leading-relaxed">
                Von einer Filiale bis zum Netzwerk mit konsistenten Standards und zentraler Steuerung.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IDAComplianceSection;
